import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import QuickAddAccount from '../components/QuickAddAccount';
import QuickAddContact from '../components/QuickAddContact';

const STAGES = [
  { key: 'prospecting',  label: 'Prospecting',  color: '#F1EFE8', text: '#444441' },
  { key: 'qualified',    label: 'Qualified',     color: '#E6F1FB', text: '#0C447C' },
  { key: 'proposal',     label: 'Proposal',      color: '#FAEEDA', text: '#633806' },
  { key: 'negotiation',  label: 'Negotiation',   color: '#FBEAF0', text: '#72243E' },
  { key: 'closed_won',   label: 'Closed won',    color: '#EAF3DE', text: '#27500A' },
  { key: 'closed_lost',  label: 'Closed lost',   color: '#FCEBEB', text: '#791F1F' },
];

const fmt = n => n == null ? '—' : Number(n).toLocaleString('en-US', { style:'currency', currency:'USD', maximumFractionDigits:0 });

export default function PipelinePage() {
  const [grouped, setGrouped] = useState({});
  const [allDeals, setAllDeals] = useState([]);
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterOwner, setFilterOwner] = useState('all');
  const [viewMode, setViewMode] = useState('board');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [showQuickAccount, setShowQuickAccount] = useState(false);
  const [showQuickContact, setShowQuickContact] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name:'', amount:'', stage:'prospecting', close_date:'',
    company_id:'', contact_id:'', owner_id:'', notes:''
  });

  const load = () => {
    setLoading(true);
    const ownerParam = filterOwner !== 'all' ? `owner_id=${filterOwner}` : '';
    api.get(`/deals${ownerParam ? '?' + ownerParam : ''}`)
      .then(r => {
        setGrouped(r.data.deals);
        setTotal(r.data.total);
        setCount(r.data.count);
        const all = [];
        Object.values(r.data.deals).forEach(arr => all.push(...arr));
        setAllDeals(all);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const loadAccounts = () => {
    api.get('/accounts?limit=200').then(r => setAccounts(r.data.accounts || [])).catch(() => {});
  };

  const loadContacts = () => {
    api.get('/contacts?limit=200').then(r => setContacts(r.data.contacts || [])).catch(() => {});
  };

  useEffect(() => {
    load();
    loadAccounts();
    loadContacts();
    api.get('/users').then(r => setUsers(r.data.users || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [filterOwner]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sorted = [...allDeals].sort((a, b) => {
    const numFields = ['amount', 'probability'];
    if (numFields.includes(sortField)) {
      return sortDir === 'asc'
        ? parseFloat(a[sortField]||0) - parseFloat(b[sortField]||0)
        : parseFloat(b[sortField]||0) - parseFloat(a[sortField]||0);
    } else if (sortField === 'close_date' || sortField === 'created_at') {
      return sortDir === 'asc'
        ? new Date(a[sortField]||0) - new Date(b[sortField]||0)
        : new Date(b[sortField]||0) - new Date(a[sortField]||0);
    } else {
      const valA = (a[sortField]||'').toLowerCase();
      const valB = (b[sortField]||'').toLowerCase();
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    }
  });

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Opportunity name is required.');
    setSaving(true);
    try {
      await api.post('/deals', {
        ...form,
        company_id: form.company_id || null,
        contact_id: form.contact_id || null,
        owner_id: form.owner_id || null,
      });
      setShowForm(false);
      setForm({ name:'', amount:'', stage:'prospecting', close_date:'', company_id:'', contact_id:'', owner_id:'', notes:'' });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create opportunity.');
    } finally {
      setSaving(false);
    }
  };

  const moveStage = async (deal, newStage, e) => {
    e.stopPropagation();
    await api.patch(`/deals/${deal.id}`, { stage: newStage });
    load();
  };

  const stageBadge = s => {
    const stage = STAGES.find(st => st.key === s);
    if (!stage) return <span className="badge badge-gray">{s}</span>;
    return <span className="badge" style={{ background: stage.color, color: stage.text }}>{stage.label}</span>;
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ color:'var(--border-md)', marginLeft:4 }}>↕</span>;
    return <span style={{ color:'var(--accent)', marginLeft:4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const SortTh = ({ field, children }) => (
    <th onClick={() => handleSort(field)}
      style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}>
      {children}<SortIcon field={field} />
    </th>
  );

  if (loading) return <div style={{ fontSize:13, color:'var(--text-tertiary)' }}>Loading pipeline…</div>;

  return (
    <>
      {/* Quick Add Modals */}
      {showQuickAccount && (
        <QuickAddAccount
          onAdd={account => {
            setAccounts(prev => [...prev, account]);
            setForm(f => ({ ...f, company_id: account.id }));
            setShowQuickAccount(false);
          }}
          onClose={() => setShowQuickAccount(false)}
        />
      )}
      {showQuickContact && (
        <QuickAddContact
          onAdd={contact => {
            setContacts(prev => [...prev, contact]);
            setForm(f => ({ ...f, contact_id: contact.id }));
            setShowQuickContact(false);
          }}
          onClose={() => setShowQuickContact(false)}
          defaultCompanyId={form.company_id}
        />
      )}

      {/* Toolbar */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ fontSize:13, color:'var(--text-secondary)' }}>
            {count} opportunities · {fmt(total)} total
          </div>
          <select value={filterOwner} onChange={e => setFilterOwner(e.target.value)}
            style={{ height:32, padding:'0 10px', border:'0.5px solid var(--border-md)',
                     borderRadius:'var(--radius)', background:'var(--bg-secondary)',
                     color:'var(--text)', fontSize:13, outline:'none' }}>
            <option value="all">All opportunities</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <div style={{ display:'flex', border:'0.5px solid var(--border-md)', borderRadius:'var(--radius)', overflow:'hidden' }}>
            <button onClick={() => setViewMode('board')}
              style={{ height:32, padding:'0 12px', fontSize:13, cursor:'pointer', border:'none',
                       background: viewMode === 'board' ? 'var(--accent)' : 'var(--bg-secondary)',
                       color: viewMode === 'board' ? '#fff' : 'var(--text-secondary)' }}>Board</button>
            <button onClick={() => setViewMode('list')}
              style={{ height:32, padding:'0 12px', fontSize:13, cursor:'pointer', border:'none',
                       background: viewMode === 'list' ? 'var(--accent)' : 'var(--bg-secondary)',
                       color: viewMode === 'list' ? '#fff' : 'var(--text-secondary)' }}>List</button>
          </div>
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : '+ New opportunity'}
          </button>
        </div>
      </div>

      {/* New Opportunity Form */}
      {showForm && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="section-title" style={{ marginBottom:14 }}>New opportunity</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <div>
              <label className="form-label">Opportunity name *</label>
              <input className="form-input" value={form.name}
                onChange={e => setForm({...form, name:e.target.value})}
                placeholder="e.g. Q3 Connector Order" />
            </div>
            <div>
              <label className="form-label">Amount ($)</label>
              <input className="form-input" type="number" value={form.amount}
                onChange={e => setForm({...form, amount:e.target.value})} placeholder="0" />
            </div>
            <div>
              <label className="form-label">Stage</label>
              <select className="form-input" value={form.stage}
                onChange={e => setForm({...form, stage:e.target.value})}>
                {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Account</label>
              <div style={{ display:'flex', gap:6 }}>
                <select className="form-input" value={form.company_id}
                  onChange={e => setForm({...form, company_id:e.target.value})}
                  style={{ flex:1 }}>
                  <option value="">— No account —</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <button className="btn" style={{ fontSize:11, height:36, padding:'0 8px', whiteSpace:'nowrap', flexShrink:0 }}
                  onClick={() => setShowQuickAccount(true)} title="Add new account">
                  + New
                </button>
              </div>
            </div>
            <div>
              <label className="form-label">Contact</label>
              <div style={{ display:'flex', gap:6 }}>
                <select className="form-input" value={form.contact_id}
                  onChange={e => setForm({...form, contact_id:e.target.value})}
                  style={{ flex:1 }}>
                  <option value="">— No contact —</option>
                  {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                </select>
                <button className="btn" style={{ fontSize:11, height:36, padding:'0 8px', whiteSpace:'nowrap', flexShrink:0 }}
                  onClick={() => setShowQuickContact(true)} title="Add new contact">
                  + New
                </button>
              </div>
            </div>
            <div>
              <label className="form-label">Owner</label>
              <select className="form-input" value={form.owner_id}
                onChange={e => setForm({...form, owner_id:e.target.value})}>
                <option value="">— Unassigned —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Close date</label>
              <input className="form-input" type="date" value={form.close_date}
                onChange={e => setForm({...form, close_date:e.target.value})} />
            </div>
            <div style={{ gridColumn:'span 2' }}>
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes}
                onChange={e => setForm({...form, notes:e.target.value})}
                placeholder="Optional notes" />
            </div>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Create opportunity'}
            </button>
            <button className="btn" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <div className="card" style={{ padding:0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <SortTh field="name">Opportunity</SortTh>
                <SortTh field="stage">Stage</SortTh>
                <SortTh field="company_name">Account</SortTh>
                <SortTh field="contact_name">Contact</SortTh>
                <SortTh field="owner_name">Owner</SortTh>
                <SortTh field="amount">Amount</SortTh>
                <SortTh field="close_date">Close date</SortTh>
                <SortTh field="created_at">Created</SortTh>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0
                ? <tr><td colSpan={8} style={{ padding:24, color:'var(--text-tertiary)', textAlign:'center' }}>
                    No opportunities yet.
                  </td></tr>
                : sorted.map(d => (
                    <tr key={d.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/pipeline/${d.id}`)}>
                      <td style={{ fontWeight:500 }}>{d.name}</td>
                      <td>{stageBadge(d.stage)}</td>
                      <td style={{ color:'var(--text-secondary)', fontSize:12 }}>{d.company_name || '—'}</td>
                      <td style={{ color:'var(--text-secondary)', fontSize:12 }}>{d.contact_name || '—'}</td>
                      <td style={{ color:'var(--text-secondary)', fontSize:12 }}>{d.owner_name || 'Unassigned'}</td>
                      <td style={{ fontWeight:500, color:'var(--accent-dark)' }}>{fmt(d.amount)}</td>
                      <td style={{ color:'var(--text-secondary)', fontSize:12 }}>
                        {d.close_date ? new Date(d.close_date).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ color:'var(--text-tertiary)', fontSize:12 }}>
                        {new Date(d.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* BOARD VIEW */}
      {viewMode === 'board' && (
        <div className="pipeline-board">
          {STAGES.map(s => {
            const deals = grouped[s.key] || [];
            return (
              <div className="pipeline-col" key={s.key}>
                <div className="pipeline-col-header" style={{ background: s.color, color: s.text }}>
                  <span>{s.label}</span>
                  <span style={{ fontWeight:400, fontSize:11 }}>{deals.length}</span>
                </div>
                {deals.map(d => (
                  <div className="deal-card" key={d.id} onClick={() => navigate(`/pipeline/${d.id}`)}>
                    <div className="deal-name">{d.name}</div>
                    <div className="deal-co">{d.contact_name || d.company_name || '—'}</div>
                    {d.owner_name && (
                      <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:4 }}>
                        👤 {d.owner_name}
                      </div>
                    )}
                    <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:6 }}>
                      Created {new Date(d.created_at).toLocaleDateString()}
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div className="deal-amount">{fmt(d.amount)}</div>
                      <select value={d.stage} onChange={e => moveStage(d, e.target.value, e)}
                        onClick={e => e.stopPropagation()}
                        style={{ fontSize:11, border:'0.5px solid var(--border)', borderRadius:4,
                                 background:'var(--bg-secondary)', color:'var(--text-secondary)', padding:'2px 4px' }}>
                        {STAGES.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
                {deals.length === 0 && (
                  <div style={{ padding:'12px', fontSize:12, color:'var(--text-tertiary)', textAlign:'center',
                                border:'0.5px dashed var(--border)', borderRadius:'var(--radius)' }}>
                    No opportunities
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}